import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SEO_BASE_URL } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Check,
  Menu,
  AlertTriangle,
  Sun,
  Wrench,
  Building2,
} from "lucide-react";

const ACCENT_GREEN = "#10B981";
const AMBER = "#D97706";
const BG_DARK = "#0F1115";
const BG_CARD = "#151922";
const BORDER = "#2A3140";
const TEXT_MUTED = "#8B92A4";
const TEXT_BODY = "#C7CBD3";
const TEXT_WHITE = "#E5E7EB";

const Marketing = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    systemsUnderManagement: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError(null);
    try {
      const { error } = await supabase.from("marketing_contact_requests").insert({
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        systems_under_management: formData.systemsUnderManagement.trim() || null,
        source: "marketing",
      });
      if (error) throw error;
      setFormSubmitted(true);
      // Notify lead via Resend (fire-and-forget; DB is source of truth)
      supabase.functions
        .invoke("notify-marketing-access-request", {
          method: "POST",
          body: {
            name: formData.name.trim(),
            email: formData.email.trim(),
            company: formData.company.trim(),
            systems_under_management: formData.systemsUnderManagement.trim() || null,
          },
        })
        .then(({ error: notifyErr }) => {
          if (notifyErr) console.warn("Marketing notify email failed:", notifyErr);
        });
    } catch (err) {
      console.error("Marketing form submit failed:", err);
      setFormError("Something went wrong. Please try again or email support@watttower.com.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Watt Tower",
    url: SEO_BASE_URL,
    logo: `${SEO_BASE_URL}/watt-tower-logo-transparent.png`,
  };

  const softwareApplicationJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Watt Tower",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "The operations platform for solar professionals. Monitor, control, automate, and bill — from one place.",
    areaServed: "US",
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG_DARK, color: TEXT_WHITE }}>
      <Helmet>
        <title>Watt Tower — Know Every System You're Responsible For</title>
        <meta
          name="description"
          content="The operations platform for solar professionals. Monitor, control, automate, and bill — from one place. Built for fleets of off-grid and hybrid solar + battery."
        />
        <link rel="icon" type="image/svg+xml" href="/watt-tower-favicon.svg" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareApplicationJsonLd) }} />
      </Helmet>

      {/* ── Header ──────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b backdrop-blur-sm" style={{ backgroundColor: `${BG_DARK}95`, borderColor: BORDER }}>
        <div className="mx-auto max-w-7xl px-4 py-4 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" aria-label="Watt Tower home">
              <img src="/watt-tower-logo-transparent.png" alt="Watt Tower" className="h-8 w-auto object-contain" />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection("features")} className="text-sm transition-colors hover:text-white" style={{ color: TEXT_BODY }}>
                Features
              </button>
              <button onClick={() => scrollToSection("how-it-works")} className="text-sm transition-colors hover:text-white" style={{ color: TEXT_BODY }}>
                How It Works
              </button>
              <Link to="/privacy" className="text-sm transition-colors hover:text-white" style={{ color: TEXT_BODY }}>Privacy</Link>
              <Link to="/terms" className="text-sm transition-colors hover:text-white" style={{ color: TEXT_BODY }}>Terms</Link>
              <a href="mailto:support@watttower.com" className="text-sm transition-colors hover:text-white" style={{ color: TEXT_BODY }}>Contact</a>
              <Link to="/login" className="text-sm font-medium transition-colors hover:text-white" style={{ color: TEXT_WHITE }}>Sign In</Link>
              <Button
                onClick={() => scrollToSection("request-access")}
                className="h-9 rounded-lg px-6 text-sm font-semibold text-wt-bg-primary hover:opacity-90"
                style={{ backgroundColor: ACCENT_GREEN }}
              >
                Request Access
              </Button>
            </nav>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden h-11 w-11 hover:bg-white/10">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] border-wt-border-default" style={{ backgroundColor: BG_DARK }}>
                <div className="flex flex-col gap-4 pt-6">
                  <button onClick={() => scrollToSection("features")} className="text-left text-sm" style={{ color: TEXT_BODY }}>Features</button>
                  <button onClick={() => scrollToSection("how-it-works")} className="text-left text-sm" style={{ color: TEXT_BODY }}>How It Works</button>
                  <Link to="/privacy" className="text-sm" style={{ color: TEXT_BODY }}>Privacy</Link>
                  <Link to="/terms" className="text-sm" style={{ color: TEXT_BODY }}>Terms</Link>
                  <a href="mailto:support@watttower.com" className="text-sm" style={{ color: TEXT_BODY }}>Contact</a>
                  <Link to="/login" className="text-sm font-medium" style={{ color: TEXT_WHITE }}>Sign In</Link>
                  <Button onClick={() => scrollToSection("request-access")} className="h-11 rounded-lg px-6 text-sm font-semibold text-wt-bg-primary w-fit" style={{ backgroundColor: ACCENT_GREEN }}>
                    Request Access
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* ── SECTION 1 — HERO ────────────────────────────────────── */}
      <section className="px-4 py-16 md:py-24 lg:px-8">
        <div className="mx-auto max-w-6xl text-center">
          <h1 className="mb-4 font-sans text-4xl font-bold leading-tight md:text-5xl lg:text-[3.25rem]">
            Know every system you're responsible for.
          </h1>
          <p className="mb-8 font-sans text-lg md:text-xl leading-relaxed" style={{ color: TEXT_BODY }}>
            The operations platform for solar professionals.<br />
            Monitor, control, automate, and bill — from one place.
          </p>
          <div className="mb-14 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              onClick={() => scrollToSection("request-access")}
              className="h-12 rounded-lg px-8 text-base font-semibold text-wt-bg-primary hover:opacity-90"
              style={{ backgroundColor: ACCENT_GREEN }}
            >
              Request Access
            </Button>
            <Button
              onClick={() => scrollToSection("how-it-works")}
              variant="outline"
              className="h-12 rounded-lg border px-8 text-base font-medium hover:bg-white/5"
              style={{ borderColor: BORDER, color: TEXT_WHITE }}
            >
              See How It Works
            </Button>
          </div>
          {/* Hero visual: real fleet dashboard screenshot */}
          <div className="mx-auto max-w-5xl rounded-xl border overflow-hidden" style={{ borderColor: BORDER }}>
            <img
              src="/marketing/hero-fleet-dashboard.webp"
              alt="Watt Tower fleet dashboard showing system overview, risk status, and energy metrics"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* ── SECTION 2 — THE PROBLEM ───────────────────────────────── */}
      <section className="border-t px-4 py-16 md:py-24 lg:px-8" style={{ borderColor: BORDER }}>
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="mb-8 font-sans text-3xl font-bold md:text-4xl">
            Your customer knew before you did.
          </h2>
          <div className="space-y-4 text-left font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
            <p>
              You've got hundreds of systems hundreds of miles apart. Mixed inverter
              and battery brands. Fragmented monitoring portals. No single pane of glass.
            </p>
            <p>
              Vendor apps? They're designed for homeowners glancing at dashboards —
              not professionals managing fleets at scale.
            </p>
            <p>
              That's why failures go silent until 2am, when your customer calls.
            </p>
            <p>
              Silent failures are a service problem. A reputation problem.
              A truck roll eating into your margins.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 3 — CAPABILITIES ──────────────────────────────── */}
      <section id="features" className="border-t px-4 py-16 md:py-24 lg:px-8" style={{ borderColor: BORDER }}>
        <div className="mx-auto max-w-6xl space-y-24">
          {/* Capability 1 — Fleet Visibility */}
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <h3 className="mb-4 font-sans text-2xl font-semibold md:text-3xl">Fleet Visibility — One View. Every System.</h3>
              <p className="mb-4 font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                Get a unified dashboard for your entire portfolio — no matter the inverter
                or battery mix. Track real-time energy flows, state of charge in actual kWh
                (not vague percentages), system reachability, and full historical data
                across all managed properties.
              </p>
              <p className="font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                Ditch the portal shuffle: no more juggling six logins to chase down issues.
                Spot silent failures before they escalate, cut truck rolls,
                and keep your reputation intact.
              </p>
            </div>
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: BORDER }}>
              <img
                src="/marketing/fleet-energy-breakdown.webp"
                alt="Fleet energy breakdown with production, consumption, and battery metrics"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>

          {/* Capability 2 — Proactive Alerting */}
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h3 className="mb-4 font-sans text-2xl font-semibold md:text-3xl">Proactive Alerting — Know Before They Call.</h3>
              <p className="mb-4 font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                Watt Tower scans every system 24/7 against 14 critical alert types —
                like overnight reserve risks, battery degradation from Texas heat,
                offline systems, solar curtailment, and more.
              </p>
              <p className="mb-4 font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                Alerts hit your inbox before customers notice, packed with context,
                confidence scores, and actionable details.
              </p>
              <p className="font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                Shift from frantic reactions to smart interventions — saving truck rolls,
                protecting warranties, and boosting your bottom line.
              </p>
            </div>
            <div className="order-1 lg:order-2 rounded-xl border overflow-hidden" style={{ borderColor: BORDER }}>
              <img
                src="/marketing/alert-email.webp"
                alt="System offline alert email notification with diagnostic details"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>

          {/* Capability 3 — Remote Control */}
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <h3 className="mb-4 font-sans text-2xl font-semibold md:text-3xl">Remote Control — Act Without Rolling a Truck.</h3>
              <p className="mb-4 font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                From the field, your couch, or anywhere else — issue commands to inverters
                and batteries across your fleet. Use pre-built one-click actions like
                Grid Guard for storm prep, Solar Harvest to maximize production,
                or Night Reserve for overnight protection. Or build custom rules
                for recurring scenarios.
              </p>
              <p className="mb-4 font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                All with secure, revocable access that respects vendor protocols.
              </p>
              <p className="font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                The result? Avoid unnecessary site visits, resolve issues in minutes,
                protect your margins, and uncover upsell opportunities from real-time data.
                No more 2am drives; just smarter, faster service that keeps customers loyal.
              </p>
            </div>
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: BORDER }}>
              <img
                src="/marketing/optimization-modes.webp"
                alt="Optimization modes including Grid Guard, Solar Harvest, Night Reserve, and more"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>

          {/* Capability 4 — Automation */}
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div className="rounded-xl border overflow-hidden order-2 lg:order-1" style={{ borderColor: BORDER }}>
              <img
                src="/marketing/energy-outlook-forecast.webp"
                alt="Energy outlook forecast with sunrise, sunset, and battery trajectory"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h3 className="mb-4 font-sans text-2xl font-semibold md:text-3xl">Automation — Set the Rules. Watt Tower Runs the Playbook.</h3>
              <p className="mb-4 font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                Craft WHEN→THEN workflows for real-world conditions: low reserve?
                Trigger a mode switch. Storm forecast incoming? Activate Grid Guard
                fleet-wide. Overnight risk flagged? Queue generator backup.
              </p>
              <p className="mb-4 font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                Our drag-and-drop builder makes it code-free and intuitive.
                Every action needs your approval, with full audit trails and easy reversals.
              </p>
              <p className="font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                Outcome: slash daily check-ins, speed up responses, and let your fleet
                run smoother — scaling your business without the constant oversight,
                all while staying firmly in control.
              </p>
            </div>
          </div>

          {/* Capability 5 — White-Label & Billing */}
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <h3 className="mb-4 font-sans text-2xl font-semibold md:text-3xl">Your Brand, Your Billing — Platform, Not Just a Tool.</h3>
              <p className="mb-4 font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                White-label Watt Tower effortlessly: upload your logo, tweak colors,
                and map your domain for a fully branded client portal. Offer dashboards,
                alerts, and automations as your own — our tech stays behind the scenes.
              </p>
              <p className="mb-4 font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                Set your own pricing for monitoring services and bill clients directly.
                We stay invisible; you own the relationship and the revenue.
              </p>
              <p className="font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                The result? Unlock net-new recurring revenue from systems you've already
                installed, strengthen client loyalty with standout post-sale support,
                and evolve from solar contractor to full-service operations powerhouse —
                without building from scratch.
              </p>
            </div>
            <div className="rounded-xl border p-6" style={{ backgroundColor: BG_CARD, borderColor: BORDER }}>
              <p className="mb-4 text-xs font-medium uppercase tracking-wider" style={{ color: TEXT_MUTED }}>Your branded portal</p>
              <div className="space-y-3">
                <div><span className="text-xs" style={{ color: TEXT_MUTED }}>Logo & colors</span><p className="text-sm mt-0.5" style={{ color: TEXT_BODY }}>Your brand</p></div>
                <div><span className="text-xs" style={{ color: TEXT_MUTED }}>Billing</span><p className="text-sm mt-0.5" style={{ color: TEXT_BODY }}>Your pricing, your clients</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 4 — WHO IT'S FOR ──────────────────────────────── */}
      <section className="border-t px-4 py-16 md:py-24 lg:px-8" style={{ borderColor: BORDER }}>
        <div className="mx-auto max-w-6xl">
          <h2 className="mb-4 text-center font-sans text-3xl font-bold md:text-4xl">Who It's For</h2>
          <p className="mx-auto max-w-2xl text-center font-sans text-base mb-4" style={{ color: TEXT_BODY }}>
            Whether you're managing 20 systems or 2,000, the problem is the same:
            too many systems, too many brands, and not enough visibility to stay
            ahead of problems.
          </p>
          <p className="mx-auto max-w-2xl text-center font-sans text-base mb-12" style={{ color: TEXT_BODY }}>
            Watt Tower was built for anyone professionally responsible for
            solar and battery systems at scale.
          </p>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { icon: Sun, title: "Solar Contractors", copy: "You install it. You own the relationship. Watt Tower keeps you informed, in control, and ahead of every service call." },
              { icon: Wrench, title: "O&M & Service Providers", copy: "You inherit the fleet. Watt Tower gives you the visibility and tools to deliver on your SLA — without inheriting the chaos." },
              { icon: Building2, title: "Multi-Site Operators", copy: "Farms, ranches, commercial properties, remote sites. If it runs on solar and you're responsible for it, Watt Tower has eyes on it." },
            ].map(({ icon: Icon, title, copy }) => (
              <div key={title} className="rounded-xl border p-6" style={{ backgroundColor: BG_CARD, borderColor: BORDER }}>
                <Icon className="mb-4 h-6 w-6" style={{ color: ACCENT_GREEN }} />
                <h3 className="mb-2 font-sans text-lg font-semibold">{title}</h3>
                <p className="font-sans text-sm leading-relaxed" style={{ color: TEXT_BODY }}>{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 5 — HOW IT WORKS ──────────────────────────────── */}
      <section id="how-it-works" className="border-t px-4 py-16 md:py-24 lg:px-8" style={{ borderColor: BORDER }}>
        <div className="mx-auto max-w-4xl">
          <h2 className="mb-3 text-center font-sans text-3xl font-bold md:text-4xl">How It Works</h2>
          <p className="mx-auto mb-12 max-w-2xl text-center font-sans text-base" style={{ color: TEXT_BODY }}>
            Watt Tower runs continuously in the background —
            no dashboards required, no manual checks.
          </p>
          <div className="space-y-10">
            {[
              { title: "Connect", copy: "Add your credentials once. Watt Tower auto-discovers every inverter and battery across every brand in your portfolio — no manual entry, no per-device setup." },
              { title: "Poll", copy: "Every 5 minutes, Watt Tower pulls telemetry from every system — energy flows, state of charge, voltage, reachability, and more. For properties with a hardware gateway installed, polling happens every 5 seconds directly over Modbus — no cloud dependency." },
              { title: "Normalize", copy: "Data from every brand — Sol-Ark, EG4, Enphase, Franklin, Tesla, and more — gets translated into a single unified schema. No context switching. No brand-specific quirks. One consistent view across your entire fleet." },
              { title: "Learn & Forecast", copy: "Watt Tower builds a per-property model for every system using historical telemetry, weather data, and learned load patterns. Expected solar production, overnight risk, battery runway, and curtailment detection — all grounded in your system's actual behavior, not generic assumptions." },
              { title: "Evaluate", copy: "Every data point is continuously evaluated against 14 alert conditions, forecast models, battery health baselines, and your custom automation rules — across your entire fleet, every 5 minutes." },
              { title: "Control", copy: "Issue commands to inverters and batteries remotely. Pre-built templates for common scenarios. Custom rules for recurring ones. Every action gated by your approval, verified on execution, and logged permanently." },
              { title: "Act", copy: "Alerts route to the right people before your customer notices. Automation executes on schedule. Audit trails capture everything. And your clients see it all through your branded portal — on your terms." },
            ].map((step, i) => (
              <div key={step.title} className="flex gap-6">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border font-mono text-sm font-semibold" style={{ backgroundColor: BG_CARD, borderColor: BORDER, color: TEXT_WHITE }}>
                  {i + 1}
                </div>
                <div>
                  <h3 className="mb-2 font-sans text-lg font-semibold">{step.title}</h3>
                  <p className="font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>{step.copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 6 — REQUEST ACCESS ────────────────────────────── */}
      <section id="request-access" className="border-t px-4 py-16 md:py-24 lg:px-8" style={{ borderColor: BORDER }}>
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-start">
            <div>
              <h2 className="mb-4 font-sans text-3xl font-bold md:text-4xl leading-tight">
                Your fleet is running right now. Take control of it.
              </h2>
              <p className="font-sans text-lg leading-relaxed" style={{ color: TEXT_BODY }}>
                Watt Tower is built for solar professionals managing systems at scale.
                Request access and we'll be in touch within one business day.
              </p>
            </div>
            <div className="min-w-0 max-w-md">
              {formSubmitted ? (
                <div className="rounded-xl border p-8 text-center" style={{ backgroundColor: BG_CARD, borderColor: `${ACCENT_GREEN}40` }}>
                  <Check className="mx-auto mb-4 h-12 w-12" style={{ color: ACCENT_GREEN }} />
                  <p className="font-sans text-lg font-semibold">We'll be in touch within one business day.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border p-6" style={{ backgroundColor: BG_CARD, borderColor: BORDER }}>
                  <div>
                    <label htmlFor="name" className="mb-1 block text-xs font-medium uppercase tracking-wider" style={{ color: TEXT_MUTED }}>Name</label>
                    <Input id="name" type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="h-11 border bg-white/5" style={{ borderColor: BORDER, color: TEXT_WHITE }} placeholder="Your name" />
                  </div>
                  <div>
                    <label htmlFor="company" className="mb-1 block text-xs font-medium uppercase tracking-wider" style={{ color: TEXT_MUTED }}>Company</label>
                    <Input id="company" type="text" required value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="h-11 border bg-white/5" style={{ borderColor: BORDER, color: TEXT_WHITE }} placeholder="Your company" />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1 block text-xs font-medium uppercase tracking-wider" style={{ color: TEXT_MUTED }}>Email</label>
                    <Input id="email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="h-11 border bg-white/5" style={{ borderColor: BORDER, color: TEXT_WHITE }} placeholder="you@company.com" />
                  </div>
                  <div>
                    <label htmlFor="systems" className="mb-1 block text-xs font-medium uppercase tracking-wider" style={{ color: TEXT_MUTED }}>Systems under management</label>
                    <Input id="systems" type="text" required value={formData.systemsUnderManagement} onChange={(e) => setFormData({ ...formData, systemsUnderManagement: e.target.value })} className="h-11 border bg-white/5 font-mono" style={{ borderColor: BORDER, color: TEXT_WHITE }} placeholder="e.g. 20, 50-100" />
                  </div>
                  {formError && (
                    <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm" style={{ borderColor: "hsl(var(--red-primary))", color: "hsl(var(--red-primary))" }}>
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span>{formError}</span>
                    </div>
                  )}
                  <Button type="submit" disabled={formSubmitting} className="h-12 w-full rounded-lg text-base font-semibold text-wt-bg-primary hover:opacity-90" style={{ backgroundColor: ACCENT_GREEN }}>
                    {formSubmitting ? "Submitting..." : "Request Access"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 7 — WHY THIS EXISTS ───────────────────────────── */}
      <section id="founder" className="border-t px-4 py-16 md:py-24 lg:px-8" style={{ borderColor: BORDER }}>
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <img
              src="/founder-photo.png"
              alt="Solar panels on ranch roof in Texas Hill Country"
              className="w-full max-w-md rounded-xl border object-cover"
              style={{ borderColor: BORDER }}
              loading="lazy"
            />
            <div>
              <h2 className="mb-6 font-sans text-3xl font-bold md:text-4xl leading-tight">
                Built by someone who needed it first.
              </h2>
              <div className="space-y-4 font-sans text-base leading-relaxed" style={{ color: TEXT_BODY }}>
                <p>
                  I have an off-grid solar system at my ranch in Texas. On the coldest
                  weekend of the year, I left a heater running while I was away.
                  My system died. Nothing told me. I was without power for two days
                  in below freezing temperatures.
                </p>
                <p>
                  I set out to build the notification system my vendor never did.
                  Simple enough. But once I started pulling real data from real systems,
                  I kept finding more — forecasting opportunities, degradation signals,
                  curtailed solar no one was measuring, fleet patterns no single
                  installer could see alone.
                </p>
                <p>
                  What started as alerts became intelligence.
                  Intelligence became control.
                  Control became a platform.
                </p>
                <p>
                  I've spent my career in technology sales and built software before —
                  but this is the problem I kept coming back to. Every solar professional
                  I talked to had the same gaps. Watt Tower exists because no one else
                  was going to fill them.
                </p>
                <p className="font-medium" style={{ color: TEXT_WHITE }}>— Russell Horton, Founder</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 8 — FOOTER ───────────────────────────────────── */}
      <footer className="border-t px-4 py-12 lg:px-8" style={{ borderColor: BORDER }}>
        <div className="mx-auto max-w-6xl flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-2">
            <p className="text-sm" style={{ color: TEXT_MUTED }}>
              © {new Date().getFullYear()} Watt Tower · DH Ranch LLC
            </p>
            <p className="text-sm" style={{ color: TEXT_MUTED }}>Built for systems you depend on.</p>
          </div>
          <nav className="flex flex-wrap gap-6">
            <Link to="/privacy" className="text-sm transition-colors hover:text-white" style={{ color: TEXT_BODY }}>Privacy</Link>
            <Link to="/terms" className="text-sm transition-colors hover:text-white" style={{ color: TEXT_BODY }}>Terms</Link>
            <a href="mailto:support@watttower.com" className="text-sm transition-colors hover:text-white" style={{ color: TEXT_BODY }}>Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Marketing;
